import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, CheckCircle2, XCircle, Send, Unplug, ArrowRight, AlertTriangle, AlertCircle, AlertOctagon, Copy, Eye, EyeOff, FileText, Hammer, Info, Play, Rocket, Search, Timer } from 'lucide-react';
import { apiClient } from '../api/client';
import { useAuth } from '../hooks/useAuth';
import jenkinsIcon from '../assets/icons/Jenkins.svg';

const JenkinsIntegration: React.FC = () => {
  const navigate = useNavigate();
  const { user, isLoading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [connected, setConnected] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  const [showSecret, setShowSecret] = useState(false);
  const [exampleJob, setExampleJob] = useState('');
  const [exampleChannel, setExampleChannel] = useState('');

  const [baseUrl, setBaseUrl] = useState('');
  const [username, setUsername] = useState('');
  const [webhookSecret, setWebhookSecret] = useState('');
  const [lastWebhookAt, setLastWebhookAt] = useState<string | null>(null);
  const [webhookEventCount, setWebhookEventCount] = useState(0);

  const [connectBaseUrl, setConnectBaseUrl] = useState('');
  const [connectUsername, setConnectUsername] = useState('');
  const [connectApiToken, setConnectApiToken] = useState('');

  const webhookUrl = `${window.location.origin}/api/webhooks/jenkins`;

  const fetchStatus = async () => {
    try {
      const data = await apiClient.getJenkinsStatus();
      if (data?.connected) {
        setConnected(true);
        setBaseUrl(data.base_url || '');
        setUsername(data.username || '');
        setWebhookSecret(data.webhook_secret || '');
        setLastWebhookAt(data.last_webhook_at || null);
        setWebhookEventCount(data.webhook_event_count || 0);
      } else {
        setConnected(false);
      }
    } catch {
      setConnected(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) fetchStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const handleConnect = async () => {
    setError(null);
    setSuccessMsg(null);
    setSaving(true);
    try {
      await apiClient.connectJenkins({
        base_url: connectBaseUrl.trim(),
        username: connectUsername.trim(),
        api_token: connectApiToken,
      });
      setSuccessMsg('Jenkins connected successfully!');
      setConnectApiToken('');
      await fetchStatus();
    } catch (err: any) {
      setError(err.response?.data || err.message || 'Failed to connect');
    } finally {
      setSaving(false);
    }
  };

  const handleDisconnect = async () => {
    setError(null);
    setSuccessMsg(null);
    try {
      await apiClient.disconnectJenkins();
      setConnected(false);
      setSuccessMsg('Jenkins disconnected.');
    } catch (err: any) {
      setError(err.response?.data || 'Failed to disconnect');
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const data = await apiClient.testJenkins();
      if (data?.success) {
        setTestResult({ success: true, message: `Connected to Jenkins (${data.job_count ?? 0} job${data.job_count !== 1 ? 's' : ''} accessible)` });
      } else {
        setTestResult({ success: false, message: data?.error || 'Connection test failed' });
      }
    } catch (err: any) {
      setTestResult({ success: false, message: err.response?.data || 'Connection test failed' });
    } finally {
      setTesting(false);
    }
  };

  const copyValue = (key: string, value: string) => {
    navigator.clipboard.writeText(value);
    setCopied(key);
    setTimeout(() => setCopied(null), 2000);
  };

  if (authLoading || loading) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-140px)]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <Alert variant="destructive">
          <XCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      {successMsg && (
        <Alert className="border-green-500 bg-green-50 dark:bg-green-950/30">
          <CheckCircle2 className="h-4 w-4 text-green-600" />
          <AlertDescription className="text-green-700 dark:text-green-400">{successMsg}</AlertDescription>
        </Alert>
      )}
      {connected ? (
        <>
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <img src={jenkinsIcon} alt="Jenkins" className="w-8 h-8 object-contain" />
              <div>
                <CardTitle className="flex items-center gap-2">
                  Jenkins
                  <Badge className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 text-xs font-normal">Connected</Badge>
                </CardTitle>
                <CardDescription>Trigger builds, wait for results, fetch console logs, react to build failures, and run AI investigations against your Jenkins controller</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              {baseUrl && (
                <div className="min-w-0">
                  <span className="text-muted-foreground">Server URL</span>
                  <p className="font-medium truncate">{baseUrl}</p>
                </div>
              )}
              {username && (
                <div>
                  <span className="text-muted-foreground">Username</span>
                  <p className="font-medium">{username}</p>
                </div>
              )}
              <div>
                <span className="text-muted-foreground">Webhook Events Received</span>
                <p className="font-medium">{webhookEventCount.toLocaleString()} event{webhookEventCount !== 1 ? 's' : ''}</p>
              </div>
              {lastWebhookAt && (
                <div>
                  <span className="text-muted-foreground">Last Webhook</span>
                  <p className="font-medium">{new Date(lastWebhookAt).toLocaleString()}</p>
                </div>
              )}
            </div>

            {testResult && (
              <Alert variant={testResult.success ? 'default' : 'destructive'} className={testResult.success ? 'border-green-500 bg-green-50 dark:bg-green-950/30' : ''}>
                {testResult.success ? <CheckCircle2 className="h-4 w-4 text-green-600" /> : <XCircle className="h-4 w-4" />}
                <AlertDescription className={testResult.success ? 'text-green-700 dark:text-green-400' : ''}>{testResult.message}</AlertDescription>
              </Alert>
            )}

            <div className="flex flex-wrap items-center gap-2">
              <Button variant="outline" size="sm" onClick={handleTest} disabled={testing} className="gap-1">
                {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                Test Connection
              </Button>
              <Button variant="outline" size="sm" onClick={handleDisconnect} className="gap-1">
                <Unplug className="w-3.5 h-3.5" /> Disconnect
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Webhook</CardTitle>
            <CardDescription>Configure Jenkins to POST build events to Kestrel so build triggers fire in real time. Actions work without this.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-sm">Webhook URL</Label>
              <div className="flex items-center gap-2 mt-1">
                <code className="flex-1 min-w-0 truncate bg-muted px-3 py-2 rounded text-sm font-mono">{webhookUrl}</code>
                <Button variant="outline" size="sm" onClick={() => copyValue('url', webhookUrl)} className="gap-1 shrink-0">
                  {copied === 'url' ? <CheckCircle2 className="w-3.5 h-3.5 text-green-600" /> : <Copy className="w-3.5 h-3.5" />}
                  {copied === 'url' ? 'Copied' : 'Copy'}
                </Button>
              </div>
            </div>
            {webhookSecret && (
              <div>
                <Label className="text-sm">Webhook Secret (send as <code className="bg-muted px-1 rounded text-[11px] font-mono">X-Kestrel-Webhook-Secret</code> header)</Label>
                <div className="flex items-center gap-2 mt-1">
                  <code className="flex-1 min-w-0 truncate bg-muted px-3 py-2 rounded text-sm font-mono">{showSecret ? webhookSecret : '••••••••••••••••••••••••'}</code>
                  <Button variant="outline" size="sm" onClick={() => setShowSecret(s => !s)} className="gap-1 shrink-0" aria-label={showSecret ? 'Hide secret' : 'Show secret'}>
                    {showSecret ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => copyValue('secret', webhookSecret)} className="gap-1 shrink-0">
                    {copied === 'secret' ? <CheckCircle2 className="w-3.5 h-3.5 text-green-600" /> : <Copy className="w-3.5 h-3.5" />}
                    {copied === 'secret' ? 'Copied' : 'Copy'}
                  </Button>
                </div>
              </div>
            )}
            <p className="text-xs text-muted-foreground">
              The easiest setup is the <strong className="text-foreground">Notification plugin</strong>: in your job's <strong className="text-foreground">Configure → Job Notifications</strong>, add an HTTP endpoint with the URL above, JSON format, and the secret header. Alternatively, add a post-build shell step that <code className="bg-muted px-1 rounded text-[11px] font-mono">curl</code>s the URL with a JSON body containing <code className="bg-muted px-1 rounded text-[11px] font-mono">job_name</code>, <code className="bg-muted px-1 rounded text-[11px] font-mono">build_number</code>, <code className="bg-muted px-1 rounded text-[11px] font-mono">phase</code> (STARTED/COMPLETED), and <code className="bg-muted px-1 rounded text-[11px] font-mono">status</code>.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Available Workflow Blocks</CardTitle>
            <CardDescription>Use these Jenkins actions and triggers in your workflows to automate CI/CD, failure response, and build orchestration.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 mb-4">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Triggers (webhook)</p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {[
                  { name: 'Build Failed', desc: 'Triggers when a build completes with FAILURE.', Icon: AlertTriangle },
                  { name: 'Build Unstable', desc: 'Triggers when a build completes UNSTABLE (e.g. test failures).', Icon: AlertCircle },
                  { name: 'Build Succeeded', desc: 'Triggers when a build completes with SUCCESS.', Icon: CheckCircle2 },
                  { name: 'Build Completed (any result)', desc: 'Triggers when a build finishes with any result.', Icon: Hammer },
                  { name: 'Build Started', desc: 'Triggers when a build starts.', Icon: Play },
                ].map(block => (
                  <div key={block.name} className="border rounded-lg p-3 space-y-1">
                    <div className="flex items-center gap-2">
                      <block.Icon className="w-4 h-4 text-muted-foreground shrink-0" />
                      <span className="font-medium text-sm min-w-0 truncate">{block.name}</span>
                    </div>
                    <p className="text-xs text-muted-foreground leading-relaxed">{block.desc}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Actions</p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {[
                  { name: 'Trigger Build', desc: 'Trigger a job build, optionally with build parameters.', Icon: Rocket },
                  { name: 'Wait for Build', desc: 'Wait for a build to complete and return its result.', Icon: Timer },
                  { name: 'Get Build Status', desc: 'Fetch a build\u2019s current status without waiting.', Icon: Info },
                  { name: 'Get Console Log', desc: 'Fetch the tail of a build\u2019s console log.', Icon: FileText },
                  { name: 'Investigate Jenkins', desc: 'AI investigates jobs, builds, and the build queue (read-only).', Icon: Search },
                ].map(block => (
                  <div key={block.name} className="border rounded-lg p-3 space-y-1">
                    <div className="flex items-center gap-2">
                      <block.Icon className="w-4 h-4 text-muted-foreground shrink-0" />
                      <span className="font-medium text-sm min-w-0 truncate">{block.name}</span>
                    </div>
                    <p className="text-xs text-muted-foreground leading-relaxed">{block.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Example Workflow</CardTitle>
            <CardDescription>Click the highlighted fields below to customize, then create the workflow.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-muted/50 rounded-lg p-4 text-sm text-muted-foreground leading-loose">
              When the{' '}
              <input
                value={exampleJob}
                onChange={e => setExampleJob(e.target.value)}
                placeholder="job name"
                style={{ width: `${Math.max(exampleJob.length || 8, 8)}ch` }}
                className="inline-block bg-cyan-200 dark:bg-cyan-800 text-cyan-900 dark:text-cyan-100 font-medium border border-cyan-400 dark:border-cyan-600 rounded px-1 py-0 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500/50 placeholder:text-cyan-600 dark:placeholder:text-cyan-300"
              />{' '}Jenkins build fails, fetch the console log, investigate the failure with AI, and post the findings to{' '}
              <input
                value={exampleChannel}
                onChange={e => setExampleChannel(e.target.value)}
                placeholder="#channel"
                style={{ width: `${Math.max(exampleChannel.length || 8, 8)}ch` }}
                className="inline-block bg-cyan-200 dark:bg-cyan-800 text-cyan-900 dark:text-cyan-100 font-medium border border-cyan-400 dark:border-cyan-600 rounded px-1 py-0 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500/50 placeholder:text-cyan-600 dark:placeholder:text-cyan-300"
              />{' '}on Slack.
            </div>
            <Button
              size="sm"
              className="gap-1.5"
              disabled={!exampleJob || !exampleChannel}
              onClick={() => navigate('/workflows/new', {
                state: {
                  suggestion: {
                    nl_prompt: `When the ${exampleJob} Jenkins build fails, fetch the console log, investigate the failure with AI, and post the findings to ${exampleChannel} on Slack.`,
                  },
                },
              })}
            >
              Create This Workflow <ArrowRight className="w-3.5 h-3.5" />
            </Button>
          </CardContent>
        </Card>
        </>
      ) : (
        <>
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <img src={jenkinsIcon} alt="Jenkins" className="w-8 h-8 object-contain" />
              <div>
                <CardTitle>Jenkins</CardTitle>
                <CardDescription>Connect Jenkins to trigger builds, wait for results, fetch console logs, react to build failures, and run AI investigations in workflows.</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="jenkins-url" className="text-sm">Server URL</Label>
              <Input
                id="jenkins-url"
                value={connectBaseUrl}
                onChange={e => setConnectBaseUrl(e.target.value)}
                placeholder="https://jenkins.example.com"
                className="mt-1 font-mono"
              />
              <p className="text-xs text-muted-foreground mt-1">
                The base URL of your Jenkins controller. It must be reachable from Kestrel.
              </p>
            </div>
            <div>
              <Label htmlFor="jenkins-username" className="text-sm">Username</Label>
              <Input
                id="jenkins-username"
                value={connectUsername}
                onChange={e => setConnectUsername(e.target.value)}
                placeholder="kestrel-bot"
                className="mt-1 font-mono"
              />
            </div>
            <div>
              <Label htmlFor="jenkins-token" className="text-sm">API Token</Label>
              <p className="text-xs text-muted-foreground mt-1 mb-2">
                Kestrel authenticates with <strong className="text-foreground">username + API token</strong> (HTTP Basic auth). Create a token in Jenkins under <strong className="text-foreground">your user → Security → API Token → Add new token</strong>. The user needs permission to read jobs and trigger builds.
              </p>
              <Input
                id="jenkins-token"
                type="password"
                value={connectApiToken}
                onChange={e => setConnectApiToken(e.target.value)}
                placeholder="Jenkins API token"
                className="mt-1 font-mono"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Stored encrypted and only used to authenticate requests to your Jenkins server.
              </p>
            </div>
            <Button onClick={handleConnect} disabled={saving || !connectBaseUrl || !connectUsername || !connectApiToken}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Connect Jenkins
            </Button>
          </CardContent>
        </Card>
        </>
      )}
    </div>
  );
};

export default JenkinsIntegration;
